import { Body, Button, ButtonGroup, H2, H3 } from "@datapay-by-datacom/design-system";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { FileDropzone } from "../../shared/components";
import { Pagination } from "../components/Pagination/Pagination";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PAY_COMPONENT_IMPORT_BASE_PATH,
  clampPageSize,
} from "../config";
import { distinctComponentNames } from "../utils/componentNames";
import { useDetectComponentColumn } from "../hooks/useDetectComponentColumn";
import { usePagination } from "../hooks/usePagination";
import { useWizard } from "../hooks/useWizard";
import { ImportRowLimitError, parseCsvStreaming, formatDuplicateHeadersWarning } from "../utils";
import { csvTextToFile } from "../utils/csvTextToFile";
import { clearWizardSession } from "../utils/wizardSession";
import { RESERVED_LC } from "../utils/mappedRows";
import styles from "./UploadPreviewScreen.module.css";

function isReservedColumn(header: string): boolean {
  return RESERVED_LC.has(header.toLowerCase());
}

/**
 * Upload & Preview screen — the entry point of the Pay Component Import wizard.
 *
 * Lets the PS user:
 * 1. Select a CSV file via drag-and-drop or file picker.
 * 2. Preview the parsed CSV (up to 10 rows) and see the distinct Component Names found.
 * 3. Optionally override the component column.
 * 4. Continue into the wizard — context handles navigation. Provider ID import
 *    options are hidden until provider integration is implemented.
 */
export function UploadPreviewScreen() {
  const { companyCode, state, updateState } = useWizard();
  const navigate = useNavigate();

  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleFileChange = (file: File | File[] | null) => {
    setParseError(null);
    if (!file || Array.isArray(file)) {
      clearWizardSession();
      updateState({
        file: null,
        csvText: null,
        preview: null,
        componentColumn: null,
        startDateColumn: null,
        endDateColumn: null,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result;
      if (typeof text !== "string") return;
      setIsParsing(true);
      try {
        // Streaming parse yields to the event loop so large files don't block
        // the UI, and enforces MAX_IMPORT_ROWS.
        const preview = await parseCsvStreaming(text);
        // Reset any prior override so the new file re-detects its component
        // and assignment-date columns.
        updateState({
          file,
          csvText: text,
          preview,
          componentColumn: null,
          startDateColumn: null,
          endDateColumn: null,
        });
      } catch (err) {
        updateState({
          file: null,
          csvText: null,
          preview: null,
          componentColumn: null,
          startDateColumn: null,
          endDateColumn: null,
        });
        setParseError(
          err instanceof ImportRowLimitError
            ? err.message
            : "Could not parse this CSV file. Check the format and try again.",
        );
      } finally {
        setIsParsing(false);
      }
    };
    reader.readAsText(file);
  };

  // Continue to the Map Columns step. Mapping (and its dry-run) happens there,
  // so the step is always shown and any saved mappings are pre-filled/reused.
  const handleContinue = () => {
    const file = state.file ?? (state.csvText ? csvTextToFile(state.csvText) : null);
    if (!file || !state.mypayEnvironment) return;
    void navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/map`);
  };

  const preview = state.preview;
  const previewRows = preview?.rows ?? [];
  const pageSize = state.pageSize ?? DEFAULT_PAGE_SIZE;
  const { page, setPage, pageCount, pageItems, start, total } = usePagination(
    previewRows,
    pageSize,
  );
  const showPager = total > PAGE_SIZE_OPTIONS[0];

  // Value-based detection: find the column whose values match known components.
  const { detectedColumn } = useDetectComponentColumn(
    companyCode,
    preview,
    state.mypayEnvironment,
  );

  // The component-identifying column currently in effect. Precedence:
  //   1. an explicit user override (state.componentColumn)
  //   2. value-based detection (a column whose values are known components)
  //   3. header-based detection from the parser ("Component Name"/"Deduction Name")
  const activeComponentColumn =
    state.componentColumn ?? detectedColumn ?? preview?.componentColumn ?? "";

  // Persist the detected column into wizard state (once, when the user hasn't
  // chosen one) so downstream steps resolve the same component column.
  useEffect(() => {
    if (state.componentColumn == null && detectedColumn) {
      updateState({ componentColumn: detectedColumn });
    }
  }, [detectedColumn, state.componentColumn, updateState]);

  // Distinct component names from the active column (recomputed when the user
  // overrides which column identifies the component).
  const componentNames: string[] = distinctComponentNames(
    preview?.rows ?? [],
    activeComponentColumn,
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Upload Pay Components CSV</H2>
        <Body>
          Upload the CSV file that contains the pay component values. The wizard will
          parse it for preview and import employee pay component data.
        </Body>
      </div>

      {/* File upload area */}
      <section className={styles.section} aria-labelledby="upload-section-heading">
        <H3 id="upload-section-heading">Select file</H3>
        <FileDropzone
          value={state.file}
          onChange={handleFileChange}
          instruction="Drop your CSV file here, or click to browse"
          subInstruction="Accepted format: CSV (.csv). The file must contain an Employee Code and Component Name column (or pick the component column below). Optional Start Date / End Date columns may use DD/MM/YYYY, DD-MM-YYYY, short years, YYYY-MM-DD, or YYYY/MM/DD."
          accept=".csv"
          multiple={false}
          buttonText="Select CSV file"
        />
        {state.file && (
          <p className={styles.selectedFile}>
            Selected: <strong>{state.file.name}</strong> (
            {(state.file.size / 1024).toFixed(1)} KB)
          </p>
        )}
        {isParsing && <p className={styles.selectedFile}>Parsing file…</p>}
        {parseError && (
          <p className={styles.warning} role="alert">
            {parseError}
          </p>
        )}
        {preview && preview.duplicateHeaders.length > 0 && (
          <p className={styles.warning} role="alert">
            {formatDuplicateHeadersWarning(preview.duplicateHeaders)}
          </p>
        )}
      </section>

      {/* Components found + component column selector, side by side */}
      {preview && (
        <>
          <div className={styles.splitRow}>
            <section
              className={styles.splitCol}
              aria-labelledby="components-heading"
            >
              <H3 id="components-heading">
                Components found ({componentNames.length})
              </H3>
              {componentNames.length > 0 ? (
                <ul
                  className={styles.deductionList}
                  aria-label="Distinct component names"
                >
                  {componentNames.map((name) => (
                    <li key={name} className={styles.deductionItem}>
                      {name}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.warning}>
                  No component column detected. Pick the column that holds the
                  component name on the right.
                </p>
              )}
            </section>

            {preview.headers.length > 0 && (
              <section
                className={styles.splitCol}
                aria-labelledby="component-column-heading"
              >
                <H3 id="component-column-heading">Component column</H3>
                <div className={styles.fieldGroup}>
                  <select
                    id="component-column"
                    className={styles.textInput}
                    value={activeComponentColumn}
                    onChange={(e) =>
                      updateState({ componentColumn: e.target.value || null })
                    }
                    aria-labelledby="component-column-heading"
                    aria-describedby="component-column-hint"
                  >
                    <option value="">
                      — auto-detect (Component Name / Deduction Name) —
                    </option>
                    {preview.headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                  <p id="component-column-hint" className={styles.fieldHint}>
                    Which CSV column names the component. Defaults to the detected
                    &quot;Component Name&quot; column; override it if your file
                    uses a different header.
                  </p>
                </div>
              </section>
            )}
          </div>

          {/* Preview table */}
          {preview.rows.length > 0 ? (
            <section className={styles.section} aria-labelledby="preview-heading">
              <H3 id="preview-heading">Preview ({total} rows)</H3>
              <div className={styles.tableWrapper} role="region" aria-label="CSV preview table" tabIndex={0}>
                <table className={styles.previewTable}>
                  <thead>
                    <tr>
                      {preview.headers.map((header) => (
                        <th
                          key={header}
                          className={
                            isReservedColumn(header)
                              ? styles.reservedColumn
                              : styles.dataColumn
                          }
                          scope="col"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((row, rowIndex) => (
                      <tr key={start + rowIndex}>
                        {preview.headers.map((header) => (
                          <td key={header} className={styles.cell}>
                            {row[header] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {showPager && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  total={total}
                  pageSize={pageSize}
                  onChange={setPage}
                  onPageSizeChange={(n) =>
                    updateState({ pageSize: clampPageSize(n) })
                  }
                />
              )}
            </section>
          ) : (
            <p className={styles.warning}>
              The CSV file contains no data rows.
            </p>
          )}
        </>
      )}

      {/* Import options (Provider ID) hidden until provider integration is implemented. */}

      {/* Actions */}
      <ButtonGroup align="right">
        <Button
          variant="primary"
          isDisabled={
            (!state.file && !state.csvText) ||
            !preview ||
            preview.rows.length === 0 ||
            state.mypayEnvironment === null
          }
          onPress={handleContinue}
        >
          Continue
        </Button>
      </ButtonGroup>
    </div>
  );
}
